import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  X,
  Zap,
  Shield,
  Users,
  Award,
  Clock,
  Sparkles,
  Star,
  TrendingUp,
  Target,
  BarChart3,
  BookOpen,
  Calculator,
  Video,
  MessageSquare,
  Download,
  ChevronRight,
} from "lucide-react";

const Section4 = () => {
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [hoveredPlan, setHoveredPlan] = useState();

  const plans = [
    {
      id: 1,
      name: "Basic",
      description: "Perfect for beginners",
      monthlyPrice: 499,
      yearlyPrice: 4990,
      yearlyDiscount: "17% off",
      features: [
        "10 Mock Tests per month",
        "Basic Performance Analytics",
        "All India Ranking",
        "Standard Question Bank",
        "Email Support",
        "Mobile App Access",
      ],
      limitations: [
        "No AI Analysis",
        "Limited Test Categories",
        "No Video Solutions",
      ],
      color: "from-blue-500 to-cyan-500",
      cta: "Start Free Trial",
      popular: false,
    },
    {
      id: 2,
      name: "Pro",
      description: "Most Popular Choice",
      monthlyPrice: 999,
      yearlyPrice: 9990,
      yearlyDiscount: "20% off",
      features: [
        "Unlimited Mock Tests",
        "AI-Powered Analytics",
        "Live All India Ranking",
        "Advanced Question Bank",
        "Video Solutions",
        "Priority Support",
        "Performance Prediction",
        "Personalized Roadmap",
      ],
      limitations: [],
      color: "from-purple-500 to-pink-500",
      cta: "Get Started Now",
      popular: true,
    },
    {
      id: 3,
      name: "Premium",
      description: "For Serious Aspirants",
      monthlyPrice: 1999,
      yearlyPrice: 19990,
      yearlyDiscount: "25% off",
      features: [
        "Everything in Pro",
        "1-on-1 Mentorship Sessions",
        "Custom Test Series",
        "Advanced Analytics Dashboard",
        "Early Access to New Features",
        "Dedicated Success Manager",
        "Offline Test Access",
        "Certified Performance Reports",
      ],
      limitations: [],
      color: "from-orange-500 to-red-500",
      cta: "Go Premium",
      popular: false,
    },
  ];

  const featuresComparison = [
    {
      feature: "AI-Powered Analytics",
      basic: "Limited",
      pro: "Advanced",
      premium: "Enterprise Grade",
    },
    {
      feature: "Mock Tests/Month",
      basic: "10",
      pro: "Unlimited",
      premium: "Unlimited + Custom",
    },
    {
      feature: "All India Ranking",
      basic: "Standard",
      pro: "Live Updates",
      premium: "Live + Historical",
    },
    {
      feature: "Video Solutions",
      basic: "❌",
      pro: "✓ All Questions",
      premium: "✓ All Questions + Live",
    },
    {
      feature: "Personal Mentorship",
      basic: "❌",
      pro: "❌",
      premium: "1-on-1 Sessions",
    },
    {
      feature: "Performance Prediction",
      basic: "❌",
      pro: "✓ 94% Accuracy",
      premium: "✓ 98% Accuracy",
    },
    {
      feature: "Priority Support",
      basic: "Email Only",
      pro: "24/7 Chat",
      premium: "Dedicated Manager",
    },
    {
      feature: "Offline Access",
      basic: "❌",
      pro: "❌",
      premium: "✓ Available",
    },
  ];

  const includedInAll = [
    "Mobile App Access",
    "Basic Performance Tracking",
    "All Exam Categories",
    "Previous Year Papers",
    "Sectional Tests",
    "Score Calculator",
    "Community Access",
    "Weekly Progress Reports",
  ];

  const testimonials = [
    {
      name: "Rahul Verma",
      plan: "Pro User",
      score: "SSC CGL: 98.7%",
      text: "Upgraded to Pro after 2 months. The AI analytics doubled my accuracy rate!",
      rating: 5,
    },
    {
      name: "Neha Singh",
      plan: "Premium User",
      score: "CAT: 99.5 Percentile",
      text: "The 1-on-1 mentorship was game-changing. Worth every penny!",
      rating: 5,
    },
    {
      name: "Amit Joshi",
      plan: "Basic → Pro",
      score: "UPSC Prelims: Cleared",
      text: "Started with Basic, moved to Pro. The unlimited tests made all the difference.",
      rating: 5,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white py-20 lg:py-28">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute left-0 top-1/3 h-96 w-96 rounded-full bg-gradient-to-r from-blue-500/5 to-purple-500/5 blur-3xl" />
        <div className="absolute bottom-1/3 right-0 h-96 w-96 rounded-full bg-gradient-to-r from-cyan-500/5 to-green-500/5 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.03),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2"
          >
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">
              CHOOSE YOUR PLAN
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-6 text-4xl font-bold text-gray-900 sm:text-5xl"
          >
            Invest in Your{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Success Story
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto max-w-3xl text-xl text-gray-600"
          >
            Flexible plans designed for every stage of your preparation journey
          </motion.p>
        </div>

        {/* Billing Toggle */}
        <div className="mb-12 flex items-center justify-center">
          <div className="inline-flex items-center gap-4 rounded-full border border-gray-300 bg-white p-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all ${
                billingCycle === "monthly"
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all ${
                billingCycle === "yearly"
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Yearly (Save up to 25%)
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="mb-16 grid gap-8 md:grid-cols-3">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              className={`relative rounded-2xl border-2 ${
                plan.popular
                  ? "scale-105 border-purple-500 shadow-2xl"
                  : "border-gray-200 shadow-lg"
              } overflow-hidden bg-gradient-to-b from-white to-gray-50 transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute left-0 right-0 top-0">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-2 text-center text-sm font-semibold text-white">
                    ⭐ MOST POPULAR
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="mb-8">
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="mb-6 text-gray-600">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold text-gray-900">
                        ₹
                        {billingCycle === "monthly"
                          ? plan.monthlyPrice
                          : plan.yearlyPrice}
                      </span>
                      <span className="text-gray-600">
                        /{billingCycle === "monthly" ? "month" : "year"}
                      </span>
                    </div>
                    {billingCycle === "yearly" && plan.yearlyDiscount && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm font-semibold text-green-600">
                          Save {plan.yearlyDiscount}
                        </span>
                        <span className="text-sm text-gray-500 line-through">
                          ₹{plan.monthlyPrice * 12}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8 space-y-4">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div
                        className={`flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r ${plan.color} mt-0.5 flex-shrink-0`}
                      >
                        <Check className="h-3 w-3 text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}

                  {/* Limitations */}
                  {plan.limitations.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      {plan.limitations.map((limit, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 text-gray-400"
                        >
                          <X className="mt-0.5 h-4 w-4 flex-shrink-0" />
                          <span className="text-sm">{limit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full rounded-xl py-3.5 text-lg font-semibold ${
                    plan.popular
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl"
                      : "bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-lg"
                  } transition-all`}
                >
                  {plan.cta}
                </motion.button>
              </div>

              {/* Hover Effect Overlay */}
              {hoveredPlan === plan.id && (
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/50 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>

        {/* All Plans Include */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-blue-50/30 p-8"
        >
          <h3 className="mb-6 text-center text-2xl font-bold text-gray-900">
            All Plans Include
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            {includedInAll.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Features Comparison Table */}
        <div className="mb-16">
          <div className="mb-8 text-center">
            <h3 className="mb-4 text-2xl font-bold text-gray-900">
              Detailed Feature Comparison
            </h3>
            <p className="text-gray-600">
              See exactly what you get with each plan
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-white">
                    <th className="min-w-[250px] p-6 text-left font-semibold text-gray-900">
                      Features
                    </th>
                    {plans.map((plan) => (
                      <th
                        key={plan.id}
                        className={`p-6 text-center font-semibold ${
                          plan.popular
                            ? "bg-purple-50/50 text-purple-600"
                            : "text-gray-900"
                        } min-w-[200px]`}
                      >
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featuresComparison.map((row, index) => (
                    <tr
                      key={index}
                      className={`border-t border-gray-200 ${
                        index % 2 === 0 ? "bg-gray-50/50" : ""
                      }`}
                    >
                      <td className="p-6 font-medium text-gray-900">
                        {row.feature}
                      </td>
                      <td className="p-6 text-center">
                        <span className="inline-flex min-h-[24px] items-center justify-center font-medium text-gray-700">
                          {row.basic}
                        </span>
                      </td>
                      <td className="p-6 text-center">
                        <span className="inline-flex min-h-[24px] items-center justify-center font-semibold text-green-600">
                          {row.pro}
                        </span>
                      </td>
                      <td className="p-6 text-center">
                        <span className="inline-flex min-h-[24px] items-center justify-center font-semibold text-blue-600">
                          {row.premium}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* User Testimonials */}
        <div className="mb-16">
          <div className="mb-8 text-center">
            <h3 className="mb-4 text-2xl font-bold text-gray-900">
              What Our Users Say
            </h3>
            <p className="text-gray-600">
              Real results from aspirants like you
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-lg"
              >
                <div className="mb-4 flex items-center gap-2">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="mb-6 italic text-gray-700">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.plan}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-blue-600">
                      {testimonial.score}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Free Trial Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-3xl bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white lg:p-12"
        >
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 text-3xl font-bold">
                Start Your 7-Day Free Trial
              </h3>
              <p className="mb-6 text-blue-100">
                Experience all Pro features for free. No credit card required.
                Cancel anytime.
              </p>
              <div className="space-y-3">
                {[
                  "Full access to all features",
                  "5 free mock tests",
                  "AI performance analysis",
                  "Priority support",
                  "No commitment required",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                      <Check className="h-3 w-3" />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white/10 p-6 backdrop-blur-sm">
              <div className="mb-6 text-center">
                <div className="mb-2 text-5xl font-bold">7</div>
                <div className="text-xl font-semibold">Days Free Trial</div>
                <div className="text-blue-200">Then ₹999/month</div>
              </div>

              <div className="space-y-4">
                <button className="w-full rounded-xl bg-white py-3.5 font-bold text-blue-600 hover:bg-blue-50">
                  Start Free Trial
                </button>
                <button className="w-full rounded-xl border-2 border-white py-3.5 font-bold hover:bg-white/10">
                  Book a Demo
                </button>
              </div>

              <div className="mt-6 text-center text-sm text-blue-200">
                <div className="flex items-center justify-center gap-2">
                  <Shield className="h-4 w-4" />
                  100% Secure • Money-Back Guarantee
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Preview */}
        <div className="mt-16 text-center">
          <p className="text-gray-600">
            Have questions?{" "}
            <a
              href="#"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              View our complete FAQ →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Section4;
